import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import FilmCard from '@/components/feature/FilmCard';
import EmptyState from '@/components/base/EmptyState';
import { getFilmById, getRelatedFilms, formatHeat, typeLabel } from '@/lib/film';
import { useFavorites } from '@/hooks/useFavorites';
import { useSeo } from '@/hooks/useSeo';
import type { Film } from '@/types/film';

const copyText = async (text: string) => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to legacy copy
  }
  try {
    const area = document.createElement('textarea');
    area.value = text;
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(area);
    return ok;
  } catch {
    return false;
  }
};

const DetailInfo = ({ film, isFavorite, onToggle, onCopy, onShareResult }: {
  film: Film;
  isFavorite: boolean;
  onToggle: () => void;
  onCopy: () => void;
  onShareResult: string;
}) => (
  <div>
    <div className="flex flex-wrap items-center gap-2">
      <span className="rounded-md bg-accent-500 px-2 py-0.5 text-xs font-semibold text-primary-950">
        {typeLabel(film.type)}
      </span>
      {film.genres.map((genre) => (
        <Link
          key={genre}
          to={`/category?type=${film.type}&genre=${encodeURIComponent(genre)}`}
          className="cursor-pointer rounded-md border border-background-50/25 px-2 py-0.5 text-xs text-background-200 transition-colors hover:border-background-50/60 hover:text-background-50"
        >
          {genre}
        </Link>
      ))}
    </div>

    <h1 className="mt-4 font-heading text-3xl font-bold leading-tight text-background-50 md:text-4xl">
      {film.title}
    </h1>
    <p className="mt-2 text-sm tracking-wide text-background-400">{film.enTitle}</p>

    <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-background-200">
      <span className="flex items-center gap-1.5 font-semibold text-accent-300">
        <i className="ri-star-fill text-sm" />
        {film.rating.toFixed(1)}
        <span className="text-xs font-normal text-background-400">/ 10</span>
      </span>
      <span className="h-3 w-px bg-background-50/25" />
      <span className="flex items-center gap-1.5 text-xs">
        <i className="ri-eye-line text-sm" />
        {formatHeat(film.heat)}
      </span>
      <span className="h-3 w-px bg-background-50/25" />
      <span className="text-xs">{film.year}</span>
    </div>

    <dl className="mt-6 grid grid-cols-1 gap-x-8 gap-y-3 text-sm sm:grid-cols-2">
      <div className="flex gap-3">
        <dt className="w-16 shrink-0 text-background-500">导演</dt>
        <dd className="text-background-100">{film.director}</dd>
      </div>
      <div className="flex gap-3">
        <dt className="w-16 shrink-0 text-background-500">地区</dt>
        <dd className="text-background-100">{film.region}</dd>
      </div>
      <div className="flex gap-3 sm:col-span-2">
        <dt className="w-16 shrink-0 text-background-500">主演</dt>
        <dd className="text-background-100">{film.cast.join(' / ')}</dd>
      </div>
      <div className="flex gap-3">
        <dt className="w-16 shrink-0 text-background-500">上映</dt>
        <dd className="text-background-100">{film.year} 年</dd>
      </div>
      <div className="flex gap-3">
        <dt className="w-16 shrink-0 text-background-500">
          {film.type === 'movie' ? '片长' : '集数'}
        </dt>
        <dd className="text-background-100">{film.duration}</dd>
      </div>
    </dl>

    <div className="mt-7 flex flex-wrap items-center gap-3">
      <button
        type="button"
        onClick={onToggle}
        className={`flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md px-5 py-2.5 text-sm font-semibold transition-colors ${
          isFavorite
            ? 'bg-background-50 text-primary-800 hover:bg-background-100'
            : 'bg-accent-500 text-primary-950 hover:bg-accent-400'
        }`}
      >
        <i className={isFavorite ? 'ri-heart-fill' : 'ri-heart-line'} />
        {isFavorite ? '已收藏' : '收藏这部作品'}
      </button>
      <button
        type="button"
        onClick={onCopy}
        className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-background-50/10"
      >
        <i className="ri-link" />
        {onShareResult || '复制分享链接'}
      </button>
      <Link
        to={`/category?type=${film.type}`}
        className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-background-50/10"
      >
        <i className="ri-arrow-left-line" />
        返回{typeLabel(film.type)}分类
      </Link>
    </div>
  </div>
);

export default function DetailPage() {
  const { id } = useParams();
  const film = getFilmById(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState('');
  const [copied, setCopied] = useState(false);

  useSeo({
    title: film ? `${film.title} - 青青草影视详情` : '作品未找到 - 青青草影视详情',
    description: film
      ? `${film.title}（${film.year}，${film.region}${typeLabel(film.type)}）：${film.synopsis} 导演 ${film.director}，主演 ${film.cast.join('、')}。`
      : '你访问的影视作品不存在或已被移除，返回青青草首页继续浏览。',
    keywords: film
      ? `${film.title},${film.title}剧情,${film.genres.join(',')},${film.director},青青草影视详情`
      : '青青草,影视详情',
  });

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(''), 2200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = window.setTimeout(() => setCopied(false), 2400);
    return () => window.clearTimeout(timer);
  }, [copied]);

  if (!film) {
    return (
      <SiteLayout>
        <div className="mx-auto w-full max-w-portal px-4 py-16 md:px-6 md:py-24">
          <EmptyState
            title="这部作品暂时找不到了"
            description="链接可能有误，或者该作品已从演示数据中移除。你可以回到首页，或去分类页继续浏览。"
            secondaryLabel="浏览电影分类"
            secondaryTo="/category?type=movie"
          />
          <div className="mt-6 text-center">
            <Link to="/" className="cursor-pointer text-sm text-primary-700 hover:text-primary-800">
              回到青青草首页
            </Link>
          </div>
        </div>
      </SiteLayout>
    );
  }

  const related = getRelatedFilms(film, 4);
  const favorite = isFavorite(film.id);

  const handleToggle = () => {
    const next = toggleFavorite(film.id);
    setToast(next ? '已加入本地收藏，刷新后仍会保留' : '已取消收藏');
  };

  const handleCopy = async () => {
    const ok = await copyText(window.location.href);
    setCopied(ok);
    setToast(ok ? '分享链接已复制' : '复制失败，请手动复制地址栏链接');
  };

  return (
    <SiteLayout>
      <div className="relative overflow-hidden bg-primary-950">
        <div className="absolute inset-0 opacity-25">
          <img
            src={film.backdrop || film.cover}
            alt=""
            aria-hidden="true"
            className="h-full w-full scale-110 object-cover object-top blur-sm"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/90 to-primary-950/70" />

        <div className="relative mx-auto w-full max-w-portal px-4 py-8 md:px-6 md:py-12">
          <nav className="mb-6 flex items-center gap-1.5 text-xs text-background-400" aria-label="面包屑">
            <Link to="/" className="cursor-pointer transition-colors hover:text-background-50">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <Link
              to={`/category?type=${film.type}`}
              className="cursor-pointer transition-colors hover:text-background-50"
            >
              {typeLabel(film.type)}
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="truncate text-background-200">{film.title}</span>
          </nav>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-12 md:gap-8">
            <div className="md:col-span-3">
              <div className="aspect-[2/3] w-full max-w-[200px] overflow-hidden rounded-lg border border-background-50/15 bg-primary-900 md:max-w-[252px]">
                <img
                  src={film.cover}
                  alt={`${film.title} 高清封面`}
                  title={`${film.title} - 青青草影视详情`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <div className="mt-3 rounded-md border border-background-50/15 bg-background-50/5 p-3">
                <p className="text-[11px] leading-relaxed text-background-400">
                  本站仅提供作品资料整理与推荐，不提供真实播放、下载或会员服务。
                </p>
              </div>
            </div>
            <div className="md:col-span-9">
              <DetailInfo
                film={film}
                isFavorite={favorite}
                onToggle={handleToggle}
                onCopy={handleCopy}
                onShareResult={copied ? '链接已复制' : ''}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-portal px-4 py-8 md:px-6 md:py-12">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
          <section className="rounded-lg border border-background-200 bg-background-50 p-5 lg:col-span-2 md:p-6">
            <h2 className="mb-3 font-heading text-lg font-bold text-foreground-950">剧情简介</h2>
            <p className="text-sm leading-7 text-foreground-600">{film.synopsis}</p>
            <p className="mt-3 text-sm leading-7 text-foreground-500">
              《{film.title}》由{film.director}执导，{film.cast.slice(0, 2).join('、')}领衔主演，
              是一部{film.year}年出品的{film.region}
              {typeLabel(film.type)}，全片{film.duration}。作品以{film.genres.join('、')}为底色，
              在类型框架里讲了一个关于选择与告别的故事。
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              {film.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-800"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </section>

          <aside className="flex flex-col gap-4">
            <div className="rounded-lg border border-background-200 bg-background-100/70 p-5">
              <h2 className="mb-3 flex items-center gap-2 font-heading text-sm font-bold text-foreground-900">
                <i className="ri-share-forward-line text-base text-primary-600" />
                分享链接
              </h2>
              <p className="mb-3 text-xs leading-relaxed text-foreground-500">
                复制下面的链接发给朋友，对方打开即可看到这部作品的详情。
              </p>
              <div className="flex items-center gap-2 rounded-md border border-background-200 bg-background-50 px-3 py-2">
                <span className="truncate text-xs text-foreground-600">{window.location.href}</span>
              </div>
              <button
                type="button"
                onClick={handleCopy}
                className="mt-3 flex w-full cursor-pointer items-center justify-center gap-2 rounded-md bg-primary-600 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
              >
                <i className="ri-file-copy-line" />
                {copied ? '已复制到剪贴板' : '复制链接'}
              </button>
            </div>

            <div className="rounded-lg border border-accent-200 bg-accent-50 p-5">
              <h2 className="mb-2 flex items-center gap-2 font-heading text-sm font-bold text-accent-900">
                <i className="ri-information-line text-base" />
                观看说明
              </h2>
              <p className="text-xs leading-relaxed text-accent-900/80">
                详情页展示的是作品的资料与简介，收藏功能保存在你的浏览器本地，
                不涉及账号登录，也不会被上传到服务器。
              </p>
            </div>
          </aside>
        </div>

        {related.length > 0 && (
          <section className="mt-10 md:mt-14">
            <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
              <div>
                <span className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-accent-600">
                  <span className="h-2 w-2 rounded-full bg-accent-500" />
                  You May Also Like
                </span>
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">相关推荐</h2>
              </div>
              <Link
                to={`/category?type=${film.type}&genre=${encodeURIComponent(film.genres[0])}`}
                className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-200 px-3 py-2 text-sm text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                更多{film.genres[0]}作品
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
              {related.map((item) => (
                <FilmCard key={item.id} film={item} />
              ))}
            </div>
          </section>
        )}
      </div>

      {toast && (
        <div className="animate-fade-up fixed bottom-6 left-1/2 z-[60] -translate-x-1/2 rounded-md border border-primary-700 bg-primary-950 px-5 py-3 text-sm text-background-50">
          {toast}
        </div>
      )}
    </SiteLayout>
  );
}